import React, { useState, useEffect } from 'react'
import { useCloudStorage } from '../../hooks/useCloudStorage'
import SimpleGoogleDriveAuth from './SimpleGoogleDriveAuth'
import { Cloud, Download, Upload, Loader, AlertCircle, Wifi, WifiOff, HardDrive, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'

/**
 * Cloud Storage Settings Component
 * Manages cloud provider connections and storage information
 */
const CloudStorageSettings = () => {
  const [storageQuota, setStorageQuota] = useState(null)
  const [loadingQuota, setLoadingQuota] = useState(false)
  
  const {
    isConnected,
    connectedProvider,
    userInfo,
    getStorageQuota
  } = useCloudStorage()

  // Load storage quota on component mount and when connection status changes
  useEffect(() => {
    const loadStorageQuota = async () => {
      // Always try to load storage quota from backend, regardless of frontend connection status
      // since we use backend-managed cloud storage with database-stored credentials
      setLoadingQuota(true)
      try {
        if (!isConnected || !connectedProvider) {
          console.log('CloudStorageSettings: Not connected to cloud storage')
          setStorageQuota(null)
          return
        }

        console.log('🔍 CloudStorageSettings: Loading storage quota using CloudStorageContext')
        const result = await getStorageQuota()
        
        if (result.success && result.quota) {
          const quota = result.quota
          // Transform the usage data to match the expected quota format
          setStorageQuota({
            totalBytes: quota.totalBytes,
            usedBytes: quota.usedBytes,
            availableBytes: quota.availableBytes || (quota.totalBytes - quota.usedBytes),
            usagePercentage: quota.usedBytes && quota.totalBytes ? (quota.usedBytes / quota.totalBytes * 100) : 0,
            usedFormatted: formatBytes(quota.usedBytes),
            totalFormatted: formatBytes(quota.totalBytes),
            availableFormatted: formatBytes(quota.availableBytes || (quota.totalBytes - quota.usedBytes)),
            providerType: connectedProvider
          })
          console.log('✅ CloudStorageSettings: Storage quota loaded successfully')
        } else {
          console.log('❌ CloudStorageSettings: Storage quota not available:', result.error)
          setStorageQuota(null)
        }
      } catch (error) {
        console.log('❌ CloudStorageSettings: Storage quota not available:', error.message)
        setStorageQuota(null)
      } finally {
        setLoadingQuota(false)
      }
    }

    loadStorageQuota()
  }, []) // Remove isConnected dependency since we use backend-managed cloud storage

  /**
   * Handle successful cloud connection
   */
  const handleConnectionSuccess = (data) => {
    toast.success('Cloud storage connected successfully!')
    
    // Show a success message about reconnection
    if (data) {
      toast.success('🔄 Google Drive reconnected! Your notes will now sync properly.', {
        duration: 5000,
        id: 'google-drive-reconnected'
      })
    }
    
    // Reload quota information
    if (data) {
      setTimeout(() => {
        // Reload storage quota using the CloudStorageContext
        const loadStorageQuota = async () => {
          if (!isConnected || !connectedProvider) {
            console.log('CloudStorageSettings: Not connected to cloud storage, skipping quota reload')
            return
          }

          setLoadingQuota(true)
          try {
            console.log('🔍 CloudStorageSettings: Reloading storage quota after connection')
            const result = await getStorageQuota()
            
            if (result.success && result.quota) {
              const quota = result.quota
              // Transform the usage data to match the expected quota format
              setStorageQuota({
                totalBytes: quota.totalBytes,
                usedBytes: quota.usedBytes,
                availableBytes: quota.availableBytes || (quota.totalBytes - quota.usedBytes),
                usagePercentage: quota.usedBytes && quota.totalBytes ? (quota.usedBytes / quota.totalBytes * 100) : 0,
                usedFormatted: formatBytes(quota.usedBytes),
                totalFormatted: formatBytes(quota.totalBytes),
                availableFormatted: formatBytes(quota.availableBytes || (quota.totalBytes - quota.usedBytes)),
                providerType: connectedProvider
              })
              console.log('✅ CloudStorageSettings: Storage quota reloaded successfully')
            } else {
              console.log('❌ CloudStorageSettings: Storage quota not available:', result.error)
              setStorageQuota(null)
            }
          } catch (error) {
            console.log('❌ CloudStorageSettings: Storage quota not available:', error.message)
            setStorageQuota(null)
          } finally {
            setLoadingQuota(false)
          }
        }
        loadStorageQuota()
      }, 1000)
    }
  }

  /**
   * Handle cloud connection error
   */
  const handleConnectionError = (error) => {
    console.error('Cloud connection error:', error)
    toast.error('Failed to connect to cloud storage')
  }

  /**
   * Format bytes to human readable format
   */
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Cloud Storage</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Connect your cloud storage accounts to sync your notes across devices.
        </p>
      </div>

      {/* Connection Status */}
      <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          {isConnected ? (
            <Wifi className="w-5 h-5 text-green-500" />
          ) : (
            <WifiOff className="w-5 h-5 text-gray-400" />
          )}
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Connection Status
          </h4>
        </div>
        
        {isConnected ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Provider:</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                {connectedProvider?.replace('_', ' ')}
              </span>
            </div>
            {userInfo?.email && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">Account:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {userInfo.email}
                </span>
              </div>
            )}
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-blue-600 dark:text-blue-400">Connected</span>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Not connected</span>
            </div>
            
            {/* Reconnection Notice */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mt-3">
              <div className="flex items-start space-x-2">
                <RefreshCw className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                    Need to reconnect?
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    If you were previously connected and lost access to your notes, 
                    reconnect your Google Drive account below to restore sync.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Storage Quota */}
      {isConnected && (
        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <HardDrive className="w-5 h-5 text-blue-500 dark:text-blue-400" />
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Storage Usage</h4>
          </div>
          
          {loadingQuota ? (
            <div className="flex items-center space-x-2">
              <Loader className="w-4 h-4 animate-spin text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Loading storage information...</span>
            </div>
          ) : storageQuota ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">Used:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatBytes(storageQuota.usedBytes)}
                </span>
              </div>
              {storageQuota.totalBytes && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Total:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatBytes(storageQuota.totalBytes)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-blue-500 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.min(storageQuota.usagePercentage || 0, 100)}%` 
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    {storageQuota.usagePercentage?.toFixed(1)}% used
                  </div>
                </>
              )}
              {storageQuota.availableBytes && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">Available:</span>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">
                    {formatBytes(storageQuota.availableBytes)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-yellow-600 dark:text-yellow-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Unable to load storage information</span>
            </div>
          )}
        </div>
      )}

      {/* Cloud Providers */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Available Providers</h4>
        <div className="space-y-3">
          {/* Google Drive */}
          <SimpleGoogleDriveAuth
            onConnectionSuccess={handleConnectionSuccess}
            onConnectionError={handleConnectionError}
          />
          
          {/* Dropbox - Coming Soon */}
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 opacity-60">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-gray-600 rounded-full flex-shrink-0">
                  <Cloud className="w-5 h-5 text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Dropbox</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Coming soon</p>
                </div>
              </div>
              <button
                disabled
                className="w-full sm:w-auto px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg cursor-not-allowed"
              >
                Coming Soon
              </button>
            </div>
          </div>

          {/* OneDrive - Coming Soon */}
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 opacity-60">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-gray-600 rounded-full flex-shrink-0">
                  <Cloud className="w-5 h-5 text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">OneDrive</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Coming soon</p>
                </div>
              </div>
              <button
                disabled
                className="w-full sm:w-auto px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg cursor-not-allowed"
              >
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">How Cloud Storage Works</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
              <li>Your notes are stored as encrypted files in your cloud storage</li>
              <li>You maintain full control and ownership of your data</li>
              <li>Sync works automatically across all your devices</li>
              <li>You can disconnect at any time and keep your data</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CloudStorageSettings 